/**
 * V108-8-17 / V108-8-18 (owner decisions 2026-10-18) — the actions on setup-gap steps, on the step row itself (never
 * behind "Show steps"), and the environment control where a preview connection shows.
 *
 *   GapActions          `scope:system`  "New system <name> isn't in any program yet": one "Add to <program>" per program
 *                                       with periods (POST …/programs/:program/boundary/systems — the existing boundary
 *                                       write; owner / admin here). Nothing joins on its own. The row stays open after
 *                                       the first add (a system often belongs in several programs), each add with Undo
 *                                       (the existing membership remove), until Done.
 *                       `github:repos`  why it is back, then the picker for the person whose sign-in is still open, else
 *                                       "1 · Add repositories on GitHub" and "2 · Pick repositories".
 *                       `read:account`  connect a read-only reader for the account (the provider's connect form).
 *                       `classify:system` V108-8-19: the classification questions in place (ClassifySystem.tsx).
 *                       Anyone who can't do it gets Ask an admin. Skip (snooze / decline with a reason) is the row's own.
 *   EnvironmentChoice   "It is production" (confirmed in place, then PATCH …/connectors/:id, owner / admin, ADR-0085
 *                       §5) and "Keep as preview" (remembered in this browser only; the server value is unchanged, so
 *                       nothing is claimed). Focus always lands on the next control, never on <body>.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import { vendorName, type SetupGapProgress } from '@aigovops/core';
import { addSystemToBoundary, removeBoundaryMembership } from '../../lib/api';
import { setConnectorEnvironment } from '../../lib/setup-api';
import { AskAdmin } from '../AskAdmin';
import { ConnectGitHubButton, GitHubRepoPicker } from '../GitHubConnect';
import { ExtLink } from './parts';
import { ClassifyActions } from './ClassifySystem';
import { connectHref } from './EnablerList';

/** GitHub's page where repositories are added to the installation (org or user account). */
export function installationSettingsHref(g: { account: string; account_type: string | null; installation_id: number | null }): string | null {
  if (typeof g.installation_id !== 'number' || !/^[A-Za-z0-9-]{1,39}$/.test(g.account)) return null;
  return g.account_type === 'Organization'
    ? `https://github.com/organizations/${g.account}/settings/installations/${g.installation_id}`
    : `https://github.com/settings/installations/${g.installation_id}`;
}

/** Focus the first element matching `sel` inside `root` once React has rendered (never leave focus on <body>). */
const focusSoon = (root: HTMLElement | null, sel: string) => window.setTimeout(() => root?.querySelector<HTMLElement>(sel)?.focus(), 0);
/** After a commit removes this control, land on a stable heading of the page (Setup steps / Sources). */
const focusHeading = (id: string) => window.setTimeout(() => { const h = document.getElementById(id); if (h) { if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1'); h.focus(); } }, 0);

function SystemScope({ gap, slug, onChanged, announce }: { gap: Extract<SetupGapProgress, { kind: 'system_scope' }>; slug: string; onChanged: () => Promise<unknown>; announce: (m: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [added, setAdded] = useState<Record<string, string>>({}); // program → membership id
  const ref = useRef<HTMLDivElement>(null);
  const prefix = `/w/${slug}/app`;
  if (!gap.programs.length) return <div className="su-acts" data-part="gap-actions"><Link className="su-btn" to={`${prefix}/programs`}>Choose a program</Link></div>;
  const add = async (p: { program: string; label: string }) => {
    if (busy) return;
    setBusy(p.program); setErr(null);
    try {
      const r = await addSystemToBoundary(slug, p.program, { system_id: gap.system_id, reason: 'Added from Setup' });
      setAdded((a) => ({ ...a, [p.program]: r.membership.membership_id }));
      announce(`${gap.name} is in ${p.label} now.`);
      focusSoon(ref.current, `[data-undo="${p.program}"]`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : `${gap.name} could not be added. Try again.`);
    } finally { setBusy(null); }
  };
  const undo = async (p: { program: string; label: string }) => {
    if (busy || !added[p.program]) return;
    setBusy(p.program); setErr(null);
    try {
      await removeBoundaryMembership(slug, p.program, added[p.program]!, 'Undone right after adding it from Setup');
      setAdded((a) => { const n = { ...a }; delete n[p.program]; return n; });
      announce(`${gap.name} is out of ${p.label} again.`);
      focusSoon(ref.current, `[data-add="${p.program}"]`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That could not be undone. Try again.');
    } finally { setBusy(null); }
  };
  const any = Object.keys(added).length > 0;
  return <div className="su-acts" data-part="gap-actions" ref={ref}>
    {gap.programs.map((p) => added[p.program]
      ? <span key={p.program} className="su-envchoice" data-part={`in-${p.program}`}><span className="su-hint">{`In ${p.label}`}</span>
        <button type="button" className="su-linkish" data-undo={p.program} aria-disabled={!!busy || undefined} onClick={() => void undo(p)}>Undo<span className="su-sr">{`: ${gap.name} in ${p.label}`}</span></button></span>
      : <Button key={p.program} compact data-add={p.program} aria-disabled={!!busy || undefined} aria-busy={busy === p.program || undefined}
        onClick={() => void add(p)}>{busy === p.program ? 'Adding…' : `Add to ${p.label}`}<span className="su-sr">{`: ${gap.name}`}</span></Button>)}
    {any ? <Button compact variant="ghost" onClick={() => { announce(`${gap.name}: done.`); void onChanged().then(() => focusHeading('su-steps-h')); }}>Done<span className="su-sr">{`: ${gap.name}`}</span></Button> : null}
    {err ? <p role="alert" className="su-err">{err}</p> : null}
  </div>;
}

export function GapActions({ gap, slug, viewerId, approver, onChanged, announce, title }: {
  gap: SetupGapProgress; slug: string; viewerId: string; approver: boolean; title: string;
  onChanged: () => Promise<unknown>; announce: (m: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const prefix = `/w/${slug}/app`;

  if (gap.kind === 'system_classify') return <ClassifyActions gap={gap} slug={slug} approver={approver} onChanged={onChanged} announce={announce} afterSave={() => focusHeading('su-steps-h')} />;

  if (gap.kind === 'system_scope') {
    if (!approver) return <div className="su-acts" data-part="gap-actions"><AskAdmin slug={slug} compact what="adds systems to a program" subject={`GAC Pro: please add ${gap.name} to a program`} /></div>;
    return <SystemScope gap={gap} slug={slug} onChanged={onChanged} announce={announce} />;
  }

  if (gap.kind === 'github_repos') {
    // Flow M (review F-8): say why the step is back.
    const why = <p className="su-hint" data-part="gap-why">{gap.reason === 'no_repositories'
      ? `The GAC Pro app on ${gap.account} can read no repository yet.` : `The repository pick on ${gap.account} wasn't saved.`}</p>;
    if (!approver) return <div data-part="gap-actions">{why}<div className="su-acts"><AskAdmin slug={slug} compact what="connects GitHub" subject={`GAC Pro: please pick the repositories for ${gap.account}`} /></div></div>;
    const mine = !!gap.pending_connector_id && gap.pending_by_user_id === viewerId;
    if (mine && picking) {
      return <div data-part="gap-actions"><GitHubRepoPicker slug={slug} connectorId={gap.pending_connector_id!}
        onSaved={() => { setPicking(false); void onChanged(); announce(`Repositories picked on ${gap.account}.`); }} onCancel={() => setPicking(false)} /></div>;
    }
    const settings = installationSettingsHref(gap);
    return <div data-part="gap-actions">{why}<div className="su-acts">
      {mine ? <Button compact onClick={() => setPicking(true)}>Pick repositories<span className="su-sr">{`: ${gap.account}`}</span></Button>
        : gap.reason === 'no_repositories' && settings
          ? <><ExtLink href={settings}>1 · Add repositories on GitHub</ExtLink><ConnectGitHubButton slug={slug} label="2 · Pick repositories" returnTo={`${prefix}/setup`} /></>
          : <ConnectGitHubButton slug={slug} label="Pick repositories" returnTo={`${prefix}/setup`} />}
    </div></div>;
  }

  // cloud_account
  const vendor = vendorName(gap.provider);
  if (!approver) return <div className="su-acts" data-part="gap-actions"><AskAdmin slug={slug} compact what="connects sources" subject={`GAC Pro: please connect ${title}`} /></div>;
  return <div className="su-acts" data-part="gap-actions">
    <Link className="su-btn" to={connectHref(slug, gap.provider)}>{`Connect ${vendor}`}<span className="su-sr">{`: ${gap.label} ${gap.native_id}`}</span></Link>
    <span className="su-hint">{gap.named_by === 'boundary' ? "In your program's boundary" : 'Found in your setup'}</span>
  </div>;
}

const KEEP_KEY = (id: string) => `gacpro-keep-preview:${id}`;
const kept = (id: string): boolean => { try { return window.localStorage.getItem(KEEP_KEY(id)) === '1'; } catch { return false; } };
const keep = (id: string, on: boolean) => { try { if (on) window.localStorage.setItem(KEEP_KEY(id), '1'); else window.localStorage.removeItem(KEEP_KEY(id)); } catch { /* storage blocked: the choice lasts this view */ } };

/** Where a preview connection shows: say it doesn't count, and let an owner / admin say it is production. */
export function EnvironmentChoice({ slug, connectorId, kind, environment, approver, onChanged, announce, compact = false, target = null }: {
  slug: string; connectorId: string; kind: string | null; environment: string; approver: boolean;
  /** What the connection reads ("account 2222-3333-4444"), named in the confirm. */
  target?: string | null;
  onChanged: () => Promise<unknown>; announce: (m: string) => void; compact?: boolean;
}) {
  const [mode, setMode] = useState<'ask' | 'confirm' | 'kept'>(() => (kept(connectorId) ? 'kept' : 'ask'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const moved = useRef(false);
  // A11y H (review): every switch of this control puts focus on its first button (never <body>).
  useEffect(() => { if (moved.current) focusSoon(ref.current, 'button'); moved.current = false; }, [mode]);
  const go = (m: typeof mode) => { moved.current = true; setMode(m); };
  const vendor = kind ? vendorName(kind) : 'This source';
  if (!approver) {
    return <span className="su-envchoice" data-part="env-choice">
      <AskAdmin slug={slug} compact what="sets a connection's environment" subject={`GAC Pro: is our ${vendor} ${environment} connection production?`} />
    </span>;
  }
  const production = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      await setConnectorEnvironment(slug, connectorId, 'production');
      keep(connectorId, false);
      announce(`${vendor} results count now.`);
      go('ask'); // the row refreshes without its preview chip; until then the question shows again
      await onChanged();
      if (!ref.current?.isConnected) focusHeading(compact ? 'su-src-h' : 'su-steps-h');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally { setBusy(false); }
  };
  if (mode === 'kept') {
    return <span className="su-envchoice" data-part="env-choice" ref={ref}>
      <span className="su-hint">Kept as preview</span>
      <button type="button" className="su-linkish" onClick={() => { keep(connectorId, false); go('ask'); }}>Change<span className="su-sr">{`: ${vendor} environment`}</span></button>
    </span>;
  }
  // Flow H (review F-6): counting a source GAC Pro saw as staging is confirmed in place, with what it means.
  if (mode === 'confirm') {
    return <span className="su-envchoice" data-part="env-choice" role="group" aria-label={`Count ${vendor} as production?`} ref={ref}>
      <span className="su-hint">{`Count ${vendor}${target ? ` ${target}` : ''} (labelled ${environment}) as production? Its results will count toward readiness.`}</span>
      <Button compact aria-disabled={busy || undefined} aria-busy={busy || undefined} onClick={() => void production()}>{busy ? 'Saving…' : 'Yes, count it'}<span className="su-sr">{`: ${vendor}`}</span></Button>
      <button type="button" className="su-linkish" onClick={() => go('ask')}>Cancel<span className="su-sr">{`: ${vendor}`}</span></button>
      {err ? <span role="alert" className="su-err">{err}</span> : null}
    </span>;
  }
  return <span className="su-envchoice" data-part="env-choice" role="group" aria-label={`Is the ${vendor} ${environment} connection production?`} ref={ref}>
    {compact ? null : <span className="su-hint">{`Reads ${environment}.`}</span>}
    <Button compact onClick={() => go('confirm')}>It is production<span className="su-sr">{`: ${vendor}`}</span></Button>
    <button type="button" className="su-linkish" onClick={() => { keep(connectorId, true); go('kept'); announce(`${vendor} stays a preview.`); }}>Keep as preview<span className="su-sr">{`: ${vendor}`}</span></button>
  </span>;
}
